<template>
  <div v-if="shouldShowRecentlyViewed" class="main-space">
    <LegacyWrap>
      <div class="title">{{ t('title') }}</div>
    </LegacyWrap>
    <div class="swiper-main-container">
      <UiSlider v-bind="cardProps" :key="experiences.length" arrows-always-visible>
        <template #skeleton>
          <ExperienceCardSkeleton is-mini />
        </template>
        <template #slide="{ slide, index }">
          <ExperienceCard
            v-experience-view-tracker="getAnalyticsParams(slide, index)"
            v-bind="toMiniCardProps(slide)"
            :position="index"
            is-mini-card
            data-test-prefix="viewed-item"
            @set-favorite="handleSetFavorite(rawExperiences[index])"
            @set-not-favorite="handleSetNotFavorite(rawExperiences[index])"
            @clicked="handleExperienceClick(slide, index)"
          />
        </template>
        <template #rightArrowIcon>
          <IconBase name="right-arrow" color="--alias-color-icon-subtle" />
        </template>
        <template #leftArrowIcon>
          <IconBase class="mirror" name="right-arrow" color="--alias-color-icon-subtle" />
        </template>
      </UiSlider>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getDisplayCurrencyQuery } from '@experience/core-display-currency-lib';
import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import { UiSlider } from '@experience/core-slider-ui';
import { ExperienceCard, ExperienceCardSkeleton } from '@experience/home-experience-card';
import { useI18n } from '@experience/i18n';
import { sendFavoriteMindboxEvent } from '@experience/mindbox-events';
import { computed, onMounted, ref } from 'vue';

import { useViewHistory } from '@/features/recentlyViewed/composables/useViewHistory';
import ru from '@/features/recentlyViewed/i18n/ru';
import { toMiniCardProps } from '@/features/recentlyViewed/lib/toMiniCardProps';
import type { Experience, ExperienceFavoriteItem } from '@/shared/api/generated/api';
import {
  addExperienceToFavorite,
  getExperiencesByIds,
  getExperiencesFavoritesList,
  removeExperienceFromFavorite,
} from '@/shared/api/generated/api';
import type {
  MainRecentlyWatchedExperienceViewEventParams,
  MainRecentlyWatchedExperienceClickEventParams,
} from '@/shared/api/generated/sogu';
import IconBase from '@/shared/icons/IconBase.vue';
import LegacyWrap from '@/shared/LegacyWrap.vue';
import { useAnalytics } from '@/shared/lib/analytics';
import { useCarouselConfig } from '@/shared/lib/carousel/useCarouselConfig';
import { mapExperienceToCard, type PreparedExperience } from '@/shared/lib/experience/mapExperienceToCard';
import { ExperienceStringTypes } from '@/shared/models/experience-types';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { useViewTracker, trackEventImmediately } = useAnalytics();

const rawExperiences = ref<Experience[]>([]);
const favorites = ref<ExperienceFavoriteItem[]>([]);
const loading = ref(true);
const isViewed = ref(true);

// Блокирует повторное переключение избранного до завершения текущего запроса.
let favoritesToggling = false;

const loadFavorites = async () => {
  const { data } = await getExperiencesFavoritesList();
  favorites.value = data ?? [];
};

const { viewHistory, loadViewHistory, sortByViewedAt } = useViewHistory();

const shouldShowRecentlyViewed = computed(() => Boolean(viewHistory.value.length) && isViewed.value);

const experiences = computed(() => rawExperiences.value.map((exp) => mapExperienceToCard(exp, favorites.value)));

const vExperienceViewTracker = useViewTracker('main:recently-watched:experience.view');

// Формирует payload просмотра и клика с отдельным контрактом для аудиогидов.
const getAnalyticsParams = (
  exp: PreparedExperience,
  index: number,
): MainRecentlyWatchedExperienceViewEventParams['params'] | MainRecentlyWatchedExperienceClickEventParams['params'] => {
  const historyItem = viewHistory.value.find((el) => el.id === exp.id);
  const timestamp = historyItem?.timestamp ?? Date.now();

  const baseParams = {
    experience_id: exp.id,
    position: index,
    full_price: exp.price,
    title: exp.title,
    city_name: exp.city,
    guide_id: exp.guideId,
    rating: exp.rating,
    original_viewed_at: timestamp,
  };

  if (exp.type === ExperienceStringTypes.INDEPENDENT_WALK) {
    return {
      ...baseParams,
      type: ExperienceStringTypes.INDEPENDENT_WALK,
    };
  }

  return {
    ...baseParams,
    type: exp.type,
    format: exp.format,
    instant_booking: exp.instantBooking,
    is_paid: exp.paidPromotion,
  };
};

// Отправляет событие клика по недавно просмотренному опыту.
const handleExperienceClick = (exp: PreparedExperience, index: number) => {
  void trackEventImmediately('main:recently-watched:experience.click', getAnalyticsParams(exp, index));
};

// Отправляет намерение и добавляет недавно просмотренную экскурсию в избранное.
const handleSetFavorite = async (experience: Experience | undefined) => {
  if (!experience || favoritesToggling) {
    return;
  }

  const { id: experienceId } = experience;
  favoritesToggling = true;

  try {
    // Фиксирует намерение пользователя до запроса favorite API.
    sendFavoriteMindboxEvent({
      action: 'add',
      bootstrapData: getBootstrapData(),
      experience,
    });

    const favoriteItem = await addExperienceToFavorite({ path: { experienceId } });

    if (favoriteItem.data) {
      favorites.value = [...favorites.value, favoriteItem.data];
    }
  } finally {
    favoritesToggling = false;
  }
};

// Отправляет намерение и удаляет недавно просмотренную экскурсию из избранного.
const handleSetNotFavorite = async (experience: Experience | undefined) => {
  if (!experience || favoritesToggling) {
    return;
  }

  const { id: experienceId } = experience;
  favoritesToggling = true;

  try {
    // Фиксирует намерение пользователя до запроса favorite API.
    sendFavoriteMindboxEvent({
      action: 'remove',
      bootstrapData: getBootstrapData(),
      experience,
    });

    await removeExperienceFromFavorite({ path: { experienceId } });
    favorites.value = favorites.value.filter((item) => item.item_id !== experienceId);
  } finally {
    favoritesToggling = false;
  }
};

const fetchData = async () => {
  loading.value = true;

  // Если нет истории просмотра, не загружаем данные
  if (!viewHistory.value.length) {
    loading.value = false;
    return;
  }

  let response;

  try {
    const idsString = viewHistory.value.map((el) => el.id).join(',');
    [response] = await Promise.all([
      getExperiencesByIds({
        query: {
          ids: idsString,
          // Показываем только доступные к бронированию экскурсии из истории просмотров.
          bookable: true,
          ...getDisplayCurrencyQuery(),
        },
      }),
      loadFavorites(),
    ]);
  } catch (e) {
    loading.value = false;
    isViewed.value = false;

    throw e;
  }

  const { data } = response;

  if (!data?.results.length) {
    isViewed.value = false;
  }

  rawExperiences.value = sortByViewedAt(data?.results ?? []);
  loading.value = false;
};

onMounted(() => {
  loadViewHistory();
  void fetchData();
});

const cardProps = useCarouselConfig<PreparedExperience>({
  slides: experiences,
  mobileMini: {
    cardWidth: 300,
    spaceBetween: 16,
    options: { initialSlide: 0, slidesOffsetBefore: 20, slidesOffsetAfter: 20 },
  },
  tabletMobile: {
    slidesPerView: 2.9,
    spaceBetween: 16,
    options: {
      initialSlide: 0,
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20,
      breakpoints: { 767: { slidesPerGroup: 2 } },
    },
  },
  desktop: {
    slidesPerView: 4,
    spaceBetween: 16,
    options: { breakpoints: { 1023: { slidesPerGroup: 4 } } },
  },
});
</script>

<style lang="scss" scoped>
.title {
  color: var(--alias-color-text-default);
  text-decoration: none;

  @include typography.base-800-750-extra;

  @include adaptivity.mobile {
    @include typography.base-700-500-extra;
  }
}

.mirror {
  transform: scaleX(-1);
}

.center {
  display: flex;
  justify-content: center;
}

.slide {
  flex-direction: column;
}

:deep(.btn-prev) {
  top: 25%;
  transform: translate(-50%, 0);

  @include adaptivity.tablet {
    display: none;
  }
}

:deep(.btn-next) {
  top: 25%;
  transform: translate(50%, 0);

  @include adaptivity.tablet {
    display: none;
  }
}

.swiper-main-container:hover {
  :deep(.btn-prev) {
    opacity: 1;
  }

  :deep(.btn-next) {
    opacity: 1;
  }
}
</style>
